/* The order a set of chosen files should be in.
 *
 * This matters more than it looks. A file picker hands over whatever order it
 * feels like, and for a batch that order decides three things at once: which
 * files survive the cap, the order of the rows in the results table, and -- now
 * that the images can be assembled into one document -- the PAGE ORDER of that
 * document. Getting scan_009 before scan_002 produces a PDF nobody wants.
 *
 * THE RULE, deliberately decided for the whole set rather than pair by pair:
 *
 *   If every name carries a number, order by those numbers.
 *   Otherwise order by the date the file was last modified.
 *   Where either is a tie, fall back to the name.
 *
 * Set-level rather than per-pair because a comparator that switches strategy
 * depending on which two files it is looking at is not a valid ordering at all:
 * it can say a < b, b < c and c < a, and then the result depends on the sort
 * implementation. One rule for the whole set is also a rule that can be stated
 * in a sentence, which matters when somebody asks why their pages came out in
 * that order.
 */

/* The number in a name, or null.
 *
 * The LAST run of digits before the extension, which is what a page number
 * actually is in practice:
 *
 *   scan_001.jpg          -> 1
 *   IMG_0002.JPG          -> 2
 *   page-12.png           -> 12
 *   2024-05-13_003.jpg    -> 3     (the date is not the page number)
 *   DSC00234.jpg          -> 234
 *   holiday.jpg           -> null
 *
 * Taking the last run rather than the first is what makes the dated example
 * come out right, and dated filenames are the common case straight off a phone.
 */
export function numberIn(name) {
    const withoutExtension = String(name || "").replace(/\.[^.]+$/, "")
    const runs = withoutExtension.match(/\d+/g)
    if (!runs || !runs.length) return null
    const n = parseInt(runs[runs.length - 1], 10)
    return Number.isFinite(n) ? n : null
}

// Names compared the way a person reads them, so image9 comes before image10.
const byName = (a, b) =>
    String(a.name || "").localeCompare(String(b.name || ""),
        undefined, {numeric: true, sensitivity: "base"})

/* Sort a copy of the list. The input is left alone, because it is usually the
 * live FileList from an <input> and callers should not have it reordered under
 * them. */
export function orderFiles(files) {
    const list = [...(files || [])]
    if (list.length < 2) return list

    const everyNameNumbered = list.every((f) => numberIn(f.name) !== null)

    if (everyNameNumbered) {
        return list.sort((a, b) => {
            const d = numberIn(a.name) - numberIn(b.name)
            return d !== 0 ? d : byName(a, b)
        })
    }

    // lastModified is not guaranteed: a File built from a clipboard paste or a
    // fetched URL has none, and some browsers report 0. Treat a missing date as
    // "unknown" and let the name decide, rather than sorting everything
    // unknown to the front of 1970.
    const stamp = (f) => (typeof f.lastModified === "number" && f.lastModified > 0
        ? f.lastModified : null)

    return list.sort((a, b) => {
        const x = stamp(a)
        const y = stamp(b)
        if (x !== null && y !== null && x !== y) return x - y
        return byName(a, b)
    })
}

/* Which strategy orderFiles() would use, for the tests and for anyone reading a
 * results table and wondering. */
export function orderingUsed(files) {
    const list = [...(files || [])]
    if (list.length < 2) return "unordered"
    return list.every((f) => numberIn(f.name) !== null) ? "numbered" : "dated"
}
